import React, { FC, useEffect, useState } from 'react';
import { Box, Grid, TextField } from '@material-ui/core';
import { Controller, useForm, Validate } from 'react-hook-form';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import Employee from '../../../../interfaces/api/Employee';

const passwordRegex = {
  regEx: /^(?=.*\d)[0-9a-zA-Z]{8,}$/,
  message: 'au moins 8 caractères, dont 1 chiffre',
};

const emailRegex = {
  regEx: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
  message: 'email invalide',
};

const validateEmail = (email: string): string | undefined =>
  !new RegExp(emailRegex.regEx).test(email) ? emailRegex.message : undefined;

export interface SettingFormOptions {
  email: string;
  firstname?: string;
  lastname?: string;
  oldPassword?: string;
  password?: string;
  confirmPassword?: string;
}

interface Props {
  user: Employee;
  onSave: (options: SettingFormOptions) => void;
}

const InformationCard: FC<Props> = ({ onSave, user }) => {
  const [formError, setFormError] = useState<string>();

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<SettingFormOptions>();

  useEffect(() => {
    reset({
      email: user.email,
      lastname: user.lastname,
      firstname: user.firstname,
    });
  }, [reset, user]);

  const validatePassword: Validate<string | undefined> = (confirmPassword?: string) => {
    const password = getValues('password');

    if (!password) {
      return;
    }

    if (confirmPassword !== password) {
      return 'Les mots de passe ne correspondent pas';
    }

    if (!new RegExp(passwordRegex.regEx).test(confirmPassword)) {
      return passwordRegex.message;
    }

    return;
  };

  const onSubmit = async (options: SettingFormOptions) => {
    try {
      await onSave(options);
      setFormError(undefined);
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <Card>
      <Grid justifyContent='center' container spacing={2}>
        <Grid item xs={12}>
          <Box fontWeight='bolder' fontSize='17px'></Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name='firstname'
            control={control}
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                value={value || ''}
                variant='outlined'
                disabled
                label='Prénom'
                fullWidth
                size='small'
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name='lastname'
            control={control}
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                value={value || ''}
                variant='outlined'
                disabled
                label='Nom'
                fullWidth
                size='small'
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='email'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' }, validate: validateEmail }}
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                value={value || ''}
                variant='outlined'
                label='Email'
                type='email'
                fullWidth
                size='small'
                error={undefined !== error}
                helperText={error ? error.message : 'Votre email Microsoft pour connecter votre calendrier Outlook'}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='oldPassword'
            control={control}
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                value={value || ''}
                variant='outlined'
                label='Mot de passe actuel'
                type='password'
                fullWidth
                size='small'
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='password'
            control={control}
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                value={value || ''}
                variant='outlined'
                label='Nouveau mot de passe'
                type='password'
                fullWidth
                size='small'
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='confirmPassword'
            control={control}
            rules={{ validate: validatePassword }}
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                value={value || ''}
                variant='outlined'
                label='Retaper le nouveau mot de passe'
                type='password'
                fullWidth
                size='small'
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box justifyContent='flex-end' display='flex'>
            <Button
              type='submit'
              color='primary'
              variant='contained'
              disabled={isSubmitting || !isDirty}
              onClick={handleSubmit(onSubmit)}
            >
              Sauvegarder
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {formError && (
            <Box component='p' color='#f44336' fontSize='.75rem' padding='0 25px'>
              {formError}
            </Box>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default InformationCard;
